/* Where things are.
 *
 * Frappe's rule for whether a DocType can be a map is exact and lives in the
 * metadata, so it is read rather than guessed: either a `location` field of
 * type `Geolocation` (which holds GeoJSON), or a `latitude` and a `longitude`
 * field. `canMap` below is that rule and nothing else.
 *
 * The tiles come from OpenStreetMap over plain <img>, with no mapping
 * library: a slippy map is a grid of 256px images at a zoom level, and Leaflet
 * would be 140KB for a screen most tenants open twice a year. Dragging and
 * the zoom buttons are the interaction; anything more belongs in the fleet
 * module, which has a real reason for it.
 */
import { useMemo, useRef, useState } from 'react'
import type { Doc, DocField, DocMeta } from './types'

export type MapKind = { kind: 'geojson'; field: string } | { kind: 'latlng' }

/** Frappe's own test, applied to our metadata. */
export function canMap(meta: DocMeta | null): MapKind | null {
  const fields: DocField[] = meta?.fields ?? []
  const has = (name: string) => fields.some((f) => f.fieldname === name)
  const geo = fields.find((f) => f.fieldname === 'location' && f.fieldtype === 'Geolocation')
  if (geo) return { kind: 'geojson', field: geo.fieldname }
  if (has('latitude') && has('longitude')) return { kind: 'latlng' }
  return null
}

/** The fields a Map needs on each row, beyond what the table shows. */
export function mapFields(kind: MapKind): string[] {
  return kind.kind === 'geojson' ? [kind.field] : ['latitude', 'longitude']
}

type Pin = { name: string; label: string; lat: number; lng: number }

/** Pull a point out of whatever shape the DocType stores. */
function pinOf(row: Doc, kind: MapKind, titleField?: string): Pin | null {
  const label = String((titleField && row[titleField]) || row.name)
  if (kind.kind === 'latlng') {
    const lat = Number(row['latitude'])
    const lng = Number(row['longitude'])
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null
    return { name: String(row.name), label, lat, lng }
  }
  // Geolocation is a GeoJSON FeatureCollection as a string. Only the first
  // point is pinned; a polygon is reduced to the first coordinate of its
  // outline, which is enough to say "it is over there".
  try {
    const parsed = JSON.parse(String(row[kind.field] ?? ''))
    for (const feature of parsed?.features ?? []) {
      const c = feature?.geometry?.coordinates
      if (!c) continue
      let point = c
      while (Array.isArray(point) && Array.isArray(point[0])) point = point[0]
      const [lng, lat] = point as [number, number]   // GeoJSON is lng first
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { name: String(row.name), label, lat, lng }
      }
    }
  } catch { /* not GeoJSON: nothing to pin */ }
  return null
}

const TILE = 256
const lngToX = (lng: number, z: number) => ((lng + 180) / 360) * TILE * 2 ** z
const latToY = (lat: number, z: number) => {
  const r = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * TILE * 2 ** z
}

export function MapView({ rows, kind, meta, onOpen }: {
  rows: Doc[]
  kind: MapKind
  meta: DocMeta | null
  onOpen?: (name: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(5)
  const [nudge, setNudge] = useState({ x: 0, y: 0 })
  const drag = useRef<{ x: number; y: number } | null>(null)

  const pins = useMemo(
    () => rows.map((r) => pinOf(r, kind, meta?.title_field)).filter(Boolean) as Pin[],
    [rows, kind, meta])

  const middle = useMemo(() => {
    if (!pins.length) return { lat: 22.35, lng: 78.66 }   // India, as a default
    return {
      lat: pins.reduce((s, p) => s + p.lat, 0) / pins.length,
      lng: pins.reduce((s, p) => s + p.lng, 0) / pins.length,
    }
  }, [pins])

  if (!pins.length) {
    return (
      <div className="card card-pad muted small">
        Nothing here has a location on it yet, so there is nothing to put on a map.
      </div>
    )
  }

  const width = 900
  const height = 460
  const centreX = lngToX(middle.lng, zoom) - nudge.x
  const centreY = latToY(middle.lat, zoom) - nudge.y
  const left = centreX - width / 2
  const top = centreY - height / 2

  const tiles: { x: number; y: number; sx: number; sy: number }[] = []
  const max = 2 ** zoom
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      if (ty < 0 || ty >= max) continue
      tiles.push({ x: ((tx % max) + max) % max, y: ty, sx: tx * TILE - left, sy: ty * TILE - top })
    }
  }

  return (
    <div className="mapv">
      <div className="row-between" style={{ marginBottom: 10 }}>
        <div className="small muted">
          {pins.length} of {rows.length} have a location
        </div>
        <div className="row" role="group" aria-label="Zoom" style={{ gap: 4 }}>
          <button type="button" className="btn btn-sm" aria-label="Zoom out"
                  onClick={() => setZoom((z) => Math.max(z - 1, 2))}>−</button>
          <button type="button" className="btn btn-sm" aria-label="Zoom in"
                  onClick={() => setZoom((z) => Math.min(z + 1, 17))}>+</button>
        </div>
      </div>

      <div
        ref={box} className="mapv-frame" style={{ height }}
        onPointerDown={(e) => {
          drag.current = { x: e.clientX, y: e.clientY }
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerMove={(e) => {
          if (!drag.current) return
          setNudge((n) => ({
            x: n.x + (e.clientX - drag.current!.x),
            y: n.y + (e.clientY - drag.current!.y),
          }))
          drag.current = { x: e.clientX, y: e.clientY }
        }}
        onPointerUp={() => { drag.current = null }}
        onPointerCancel={() => { drag.current = null }}
      >
        {tiles.map((t) => (
          <img key={`${t.x}/${t.y}`} alt="" aria-hidden="true" draggable={false}
               className="mapv-tile" style={{ left: t.sx, top: t.sy }}
               src={`https://tile.openstreetmap.org/${zoom}/${t.x}/${t.y}.png`} />
        ))}

        {pins.map((p) => {
          const x = lngToX(p.lng, zoom) - left
          const y = latToY(p.lat, zoom) - top
          if (x < -40 || y < -40 || x > width + 40 || y > height + 40) return null
          return (
            <button key={p.name} type="button" className="mapv-pin" style={{ left: x, top: y }}
                    onClick={() => onOpen?.(p.name)}
                    aria-label={`${p.label}, at ${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`}
                    title={p.label}>
              <span aria-hidden="true" />
            </button>
          )
        })}

        {/* OpenStreetMap's licence asks for this, and it is not optional. */}
        <a className="mapv-credit" href="https://www.openstreetmap.org/copyright"
           target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>
      </div>

      <ul className="mapv-list">
        {pins.slice(0, 50).map((p) => (
          <li key={p.name}>
            <button type="button" className="linkish" onClick={() => onOpen?.(p.name)}>
              {p.label}
            </button>
            <span className="small muted"> · {p.lat.toFixed(4)}, {p.lng.toFixed(4)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
